import { createContext, useContext, useEffect } from 'react';
import { useMobileDetect, useMobileViewportHeight, usePreventIOSZoom } from '@/hooks/useMobileDetect';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

// Context para informações do dispositivo
const DeviceContext = createContext(null);

export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevice must be used within a ResponsiveProvider');
  }
  return context;
};

/**
 * Provider que disponibiliza informações do dispositivo para toda a aplicação
 */
export const ResponsiveProvider = ({ children }) => {
  const deviceInfo = useMobileDetect();
  const breakpoint = useBreakpoint();
  
  // Aplica ajustes de viewport para mobile
  useMobileViewportHeight();
  
  // Previne zoom no iOS
  usePreventIOSZoom();

  // Aplica classes CSS no body baseado no dispositivo
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;

    // Remove classes anteriores
    body.classList.remove('is-mobile', 'is-tablet', 'is-desktop', 'is-touch', 'is-ios', 'is-android', 'is-pwa');
    html.classList.remove('is-mobile', 'is-tablet', 'is-desktop');

    // Adiciona classes baseadas no dispositivo
    if (deviceInfo.isMobile) {
      body.classList.add('is-mobile');
      html.classList.add('is-mobile');
    }
    if (deviceInfo.isTablet) {
      body.classList.add('is-tablet');
      html.classList.add('is-tablet');
    }
    if (deviceInfo.isDesktop) {
      body.classList.add('is-desktop');
      html.classList.add('is-desktop');
    }
    if (deviceInfo.hasTouch) body.classList.add('is-touch');
    if (deviceInfo.isIOS) body.classList.add('is-ios');
    if (deviceInfo.isAndroid) body.classList.add('is-android');
    if (deviceInfo.isPWA) body.classList.add('is-pwa');

    // Adiciona orientação
    body.classList.remove('is-landscape', 'is-portrait');
    body.classList.add(`is-${deviceInfo.orientation}`);

  }, [deviceInfo]);

  const value = {
    ...deviceInfo,
    ...breakpoint
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};

/**
 * Container responsivo que ajusta automaticamente o layout
 */
export const ResponsiveContainer = ({ 
  children, 
  className,
  mobileClassName,
  tabletClassName,
  desktopClassName,
  as: Component = 'div',
  ...props 
}) => {
  const { isMobile, isTablet, isDesktop } = useDevice();

  const responsiveClass = cn(
    className,
    isMobile && mobileClassName,
    isTablet && tabletClassName,
    isDesktop && desktopClassName
  );

  return (
    <Component className={responsiveClass} {...props}>
      {children}
    </Component>
  );
};

/**
 * Componente que renderiza apenas em dispositivos específicos
 */
export const ShowOnMobile = ({ children }) => {
  const { isMobile } = useDevice();
  return isMobile ? children : null;
};

export const ShowOnTablet = ({ children }) => {
  const { isTablet } = useDevice();
  return isTablet ? children : null;
};

export const ShowOnDesktop = ({ children }) => {
  const { isDesktop } = useDevice();
  return isDesktop ? children : null;
};

export const HideOnMobile = ({ children }) => {
  const { isMobile } = useDevice();
  return !isMobile ? children : null;
};

export const HideOnDesktop = ({ children }) => {
  const { isDesktop } = useDevice();
  return !isDesktop ? children : null;
};

/**
 * Hook para obter classes responsivas
 */
export const useResponsiveClass = (mobileClass, tabletClass, desktopClass) => {
  const { isMobile, isTablet, isDesktop } = useDevice();
  
  if (isMobile) return mobileClass;
  if (isTablet) return tabletClass;
  return desktopClass;
};

export default ResponsiveContainer;
